// Contacts → bulk assign (client, 2026-11-05). Admin / Super Admin pick one or
// more users; the selected contacts are split between them equally, and each
// client's leads, orders and invoices move with it (the same cascade as an
// approved single-contact reassignment). Each new owner gets one notification.
import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { UserPlus } from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { MultiSelect } from '@/shared/components/ui/MultiSelect';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { useUserOptions } from '@/shared/hooks/useUserOptions';

interface Props { open: boolean; onClose: () => void; ids: string[]; onDone: () => void }
interface AssignResult { contacts: number; pipelineRecords: number; orders: number; invoices: number; perUser: Array<{ name: string; contacts: number; leads: number }> }

export function ContactsBulkAssignModal({ open, onClose, ids, onDone }: Props) {
  const qc = useQueryClient();
  const { data: users = [] } = useUserOptions(open);
  const [to, setTo] = useState<string[]>([]);
  // Salespeople first — they are who these go to.
  const options = useMemo(() => [...users]
    .sort((a, b) => Number(b.department === 'sales') - Number(a.department === 'sales') || a.display_name.localeCompare(b.display_name))
    .map((u) => ({ value: u.id, label: `${u.display_name}${u.username ? ` (${u.username})` : ''}` })), [users]);
  const each = to.length ? Math.ceil(ids.length / to.length) : 0;

  const assign = useMutation({
    mutationFn: async () => (await api.post<AssignResult>('/api/contacts/bulk/assign', { ids, toUserIds: to })).data,
    onSuccess: (r) => {
      toast.success(`${r.contacts} contact(s) assigned — ${r.perUser.map((p) => `${p.name} ${p.contacts}`).join(', ')}. ${r.pipelineRecords} lead(s) moved with them.`, { duration: 8000 });
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['contacts-bulk'] });
      setTo([]);
      onDone();
      onClose();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not assign the contacts.')),
  });

  return (
    <Modal open={open} onClose={onClose} title={`Assign ${ids.length} contact${ids.length === 1 ? '' : 's'}`} size="md"
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button disabled={!to.length || assign.isPending} onClick={() => assign.mutate()}>
          <UserPlus className="h-4 w-4" /> {assign.isPending ? 'Assigning…' : `Assign to ${to.length || ''} user${to.length === 1 ? '' : 's'}`}
        </Button>
      </>}>
      <div className="space-y-3 text-sm">
        <label className="block text-xs font-semibold text-on-surface-variant">Assign to
          <div className="mt-1"><MultiSelect options={options} value={to} onChange={setTo} placeholder="Choose one or more users…" /></div>
        </label>
        {to.length > 1 && <p className="text-xs text-on-surface-variant">Split equally — about <b>{each}</b> each.</p>}
        <ul className="list-disc space-y-1 pl-5 text-xs text-on-surface-variant">
          <li>Each client becomes the new owner's, with its <b>leads, quotations, orders and invoices</b> — the same as an approved reassignment.</li>
          <li>Each new owner gets one notification with how many they received.</li>
        </ul>
      </div>
    </Modal>
  );
}
